import {
  Building2, LineChart, KeyRound, Home, Briefcase, FileSearch, Compass,
  Scale, BookOpen, Stethoscope, Activity, HeartPulse, Bell, UtensilsCrossed,
  Truck, Package, HardHat, Ruler, GraduationCap, PencilLine, ScanLine,
  PiggyBank, ShoppingBag, Store, Factory, Cog, Car, Wrench, Sparkles,
  Dumbbell, CalendarDays, Ticket, Users, UserPlus, ShieldCheck, Umbrella,
  Sprout, Tractor, Newspaper, HeartHandshake, Lightbulb, Boxes
} from "lucide-react";

export const ICONS = {
  Building2, LineChart, KeyRound, Home, Briefcase, FileSearch, Compass,
  Scale, BookOpen, Stethoscope, Activity, HeartPulse, Bell, UtensilsCrossed,
  Truck, Package, HardHat, Ruler, GraduationCap, PencilLine, ScanLine,
  PiggyBank, ShoppingBag, Store, Factory, Cog, Car, Wrench, Sparkles,
  Dumbbell, CalendarDays, Ticket, Users, UserPlus, ShieldCheck, Umbrella,
  Sprout, Tractor, Newspaper, HeartHandshake, Lightbulb
};

export const getIcon = (name) => ICONS[name] || Boxes;

const norm = (s) => (s || "").trim().toLowerCase();

// Explicit build-time lookup: raw sector -> filter bucket. Unmapped sectors
// default to "Services" and are logged so nothing vanishes from the grid.
const SECTOR_BUCKET = {
  "real estate": "Real Estate",
  "private office": "Private Office",
  "logistics": "Operations",
  "construction": "Operations",
  "manufacturing": "Operations",
  "automotive": "Operations",
  "agriculture": "Operations",
  "retail": "Operations"
};

export const FILTER_PILLS = ["All", "Confirmed Portfolio", "Real Estate", "Private Office", "Operations", "Services"];

export const bucketFor = (sector) => {
  const hit = SECTOR_BUCKET[norm(sector)];
  if (!hit) {
    if (!bucketFor._warned) bucketFor._warned = new Set();
    if (!bucketFor._warned.has(sector)) {
      bucketFor._warned.add(sector);
      console.info(`[concept_data] sector "${sector}" unmapped — defaulting to Services bucket`);
    }
    return "Services";
  }
  return hit;
};

export async function loadConcepts() {
  const res = await fetch(`${process.env.PUBLIC_URL || ""}/concept_data.json`);
  const data = await res.json();
  const concepts = data.concepts || [];

  // Normalized CONFIRMED matching with guard (trim + case-insensitive)
  const titles = new Set(concepts.map((c) => norm(c.title)));
  const confirmedSet = new Set();
  (data.confirmedTitles || []).forEach((t) => {
    if (titles.has(norm(t))) confirmedSet.add(norm(t));
    else console.warn(`[concept_data] confirmedTitle "${t}" has no matching concept title`);
  });
  const confirmedCount = concepts.filter((c) => confirmedSet.has(norm(c.title))).length;
  if (data.totals?.confirmed != null && confirmedCount !== data.totals.confirmed) {
    console.warn(`[concept_data] confirmed count ${confirmedCount} != totals.confirmed ${data.totals.confirmed}`);
  }

  const enriched = concepts.map((c) => ({
    ...c,
    confirmed: confirmedSet.has(norm(c.title)),
    bucket: bucketFor(c.sector)
  }));

  const sectors = [...new Set(concepts.map((c) => c.sector))].sort();
  const tags = [...new Set(concepts.flatMap((c) => c.tags || []))].sort();

  return { totals: data.totals || {}, concepts: enriched, sectors, tags };
}

export const INDIVIDUAL = "Individual / Personal project";

export function matchConcepts(concepts, sector, tag) {
  const isIndividual = sector === INDIVIDUAL;
  const scored = concepts.map((c) => {
    let score = 0;
    if (sector && sector !== "any" && !isIndividual && c.sector === sector) score += 2;
    if (isIndividual && (c.tags || []).includes("personalization")) score += 1;
    if (tag && tag !== "any" && (c.tags || []).includes(tag)) score += 2;
    if (c.confirmed) score += 0.5;
    return { concept: c, score };
  });
  return scored
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score || a.concept.ref.localeCompare(b.concept.ref))
    .slice(0, 3)
    .map((s) => s.concept);
}

export const TAG_LABELS = {
  "lead-qualification": "Qualifying leads",
  "scheduling": "Scheduling & bookings",
  "follow-up": "Follow-up & chasing",
  "reporting": "Reports & briefings",
  "document-ai": "Documents & paperwork",
  "communication": "Client communication",
  "admin-automation": "Admin automation",
  "market-intel": "Market intelligence",
  "matching": "Matching people or things",
  "research": "Research",
  "search": "Search & retrieval",
  "personalization": "Personalisation",
  "forecasting": "Forecasting",
  "optimization": "Optimisation",
  "compliance": "Compliance & records",
  "upsell": "Upselling"
};

export const tagLabel = (t) => TAG_LABELS[t] || t;
